import React, { useState, useRef, useEffect } from 'react';
import { galleryApi } from '../../services/gallery';
import { useAuth } from '../../context/AuthContext';

export const UploadModal = ({ isOpen, onClose, onUploadSuccess }) => {
  const { user } = useAuth();

  // Состояния файлов
  const [photos, setPhotos] = useState([]);
  const [coverPhoto, setCoverPhoto] = useState(null);
  const [previews, setPreviews] = useState([]);
  const [coverPreview, setCoverPreview] = useState('');

  const [types, setTypes] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagSearch, setTagSearch] = useState('');

  // Состояния формы
  const [formData, setFormData] = useState({
    name_photo: '',
    fk_type: '',
    data_time: '',
    fk_group_photo: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);

  // Загрузка типов и тегов из БД при открытии модалки
  useEffect(() => {
    if (isOpen) {
      // Загрузка типов
      galleryApi.getContentTypes()
        .then(data => {
          const typesData = Array.isArray(data) ? data : (data.types || []);
          console.log('✅ Типы загружены:', typesData);
          setTypes(typesData);
        })
        .catch(err => console.error('❌ Ошибка загрузки типов:', err));

      // Загрузка тегов
      galleryApi.getTags()
        .then(data => {
          const tagsData = Array.isArray(data) ? data : (data.tags || []);
          console.log('✅ Теги загружены:', tagsData);
          setTags(tagsData);
        })
        .catch(err => console.error('❌ Ошибка загрузки тегов:', err));
    }
  }, [isOpen]);

  // Блокировка скролла фона
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Обработчики файлов
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'));
    if (!files.length) return;
    setPhotos(prev => [...prev, ...files]);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => setPreviews(prev => [...prev, { file, src: ev.target.result }]);
      reader.readAsDataURL(file);
    });
  };

  const handleCoverSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setCoverPhoto(file);
      const reader = new FileReader();
      reader.onload = (ev) => setCoverPreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeCover = () => {
    setCoverPhoto(null);
    setCoverPreview('');
    if (coverInputRef.current) coverInputRef.current.value = '';
  };

  // Теги
  const toggleTag = (tagId) => {
    setSelectedTags(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const filteredTags = tags.filter(t =>
    t.title.toLowerCase().includes(tagSearch.toLowerCase())
  );

  // Проверка: является ли выбранный тип "фотоальбомом"
  const isAlbumType = () => {
    const selected = types.find(t => t.pk_type == formData.fk_type);
    return selected?.title?.toLowerCase().includes('альбом');
  };

  // Отправка формы
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (!photos.length) {
      setError('Выберите хотя бы одну фотографию');
      return;
    }
    if (!formData.fk_type) {
      setError('Выберите тип контента');
      return;
    }
    if (isAlbumType() && !coverPhoto) {
      setError('Для фотоальбома необходимо загрузить обложку');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name_photo: formData.name_photo || (photos[0]?.name || ''),
        fk_type: formData.fk_type,
        data_time: formData.data_time || null,
        fk_group_photo: formData.fk_group_photo || null,
        tags: selectedTags,
        is_album: isAlbumType()
      };

      const filesToSend = isAlbumType() && coverPhoto
        ? [coverPhoto, ...photos]
        : photos;

      await galleryApi.uploadPhotos(filesToSend, payload, user?.token);

      setSuccessMsg('✅ Фотографии успешно загружены!');
      setTimeout(() => {
        onUploadSuccess?.();
        handleClose();
      }, 1200);
    } catch (err) {
      setError(err.message || 'Ошибка при загрузке');
      console.error('Upload error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPhotos([]);
    setCoverPhoto(null);
    setPreviews([]);
    setCoverPreview('');
    setSelectedTags([]);
    setTagSearch('');
    setFormData({ name_photo: '', fk_type: '', data_time: '', fk_group_photo: '' });
    setError('');
    setSuccessMsg('');
    onClose();
  };

  if (!isOpen || !user) return null;

  return (
    <div className="modal-overlay active" onClick={handleClose}>
      <div className="modal-card upload-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-close" onClick={handleClose} />
        <h2 className="modal-title">Добавить в архив</h2>

        <form onSubmit={handleSubmit}>
          {/* Загрузка фото */}
          <div className="form-group">
            <label className="form-label">Фотографии *</label>
            <div
              className="upload-dropzone"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
              onDragLeave={e => e.currentTarget.classList.remove('dragover')}
              onDrop={e => {
                e.preventDefault();
                e.currentTarget.classList.remove('dragover');
                const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
                handleFileSelect({ target: { files } });
              }}
            >
              <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileSelect} hidden />
              <div className="upload-placeholder">
                <span className="upload-icon">📷</span>
                <p>Перетащите файлы или кликните для выбора</p>
                <small>JPG, PNG, WEBP до 10 МБ</small>
              </div>
            </div>
          </div>

          {previews.length > 0 && (
            <div className="upload-previews">
              {previews.map((p, i) => (
                <div key={i} className="upload-preview-item">
                  <img src={p.src} alt={p.file.name} />
                  <button type="button" className="upload-remove" onClick={() => removePhoto(i)}>×</button>
                  <span className="upload-filename">{p.file.name}</span>
                </div>
              ))}
            </div>
          )}

          {/* Тип контента (из БД) — ✅ ИСПРАВЛЕНО: используем types, не availableTypes */}
          <div className="form-group">
            <label className="form-label">Тип *</label>
            <select
              className="form-input"
              value={formData.fk_type}
              onChange={e => setFormData(prev => ({ ...prev, fk_type: e.target.value }))}
            >
              <option value="">Выберите тип</option>
              {types.map(type => (
                <option key={type.pk_type} value={type.pk_type}>{type.title}</option>
              ))}
            </select>
          </div>

          {/* Обложка для альбома */}
          {isAlbumType() && (
            <div className="form-group">
              <label className="form-label">Обложка альбома *</label>
              {coverPreview ? (
                <div className="cover-preview-wrapper">
                  <img src={coverPreview} alt="Cover" className="cover-preview" />
                  <button type="button" className="upload-remove" onClick={removeCover}>×</button>
                </div>
              ) : (
                <div
                  className="upload-dropzone cover-dropzone"
                  onClick={() => coverInputRef.current?.click()}
                >
                  <input ref={coverInputRef} type="file" accept="image/*" onChange={handleCoverSelect} hidden />
                  <div className="upload-placeholder">
                    <span className="upload-icon">🖼️</span>
                    <p>Загрузить обложку</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Название */}
          <div className="form-group">
            <label className="form-label">Название</label>
            <input
              className="form-input"
              type="text"
              value={formData.name_photo}
              onChange={e => setFormData(prev => ({ ...prev, name_photo: e.target.value }))}
              placeholder="Название фотографии или альбома"
            />
          </div>

          {/* Дата */}
          <div className="form-group">
            <label className="form-label">Дата события</label>
            <input
              className="form-input"
              type="date"
              value={formData.data_time}
              onChange={e => setFormData(prev => ({ ...prev, data_time: e.target.value }))}
            />
          </div>

          {/* Теги — ✅ ИСПРАВЛЕНО: используем tags, не availableTags */}
          <div className="form-group">
            <label className="form-label">Теги</label>
            <input
              className="form-input"
              type="text"
              placeholder="Поиск тегов..."
              value={tagSearch}
              onChange={e => setTagSearch(e.target.value)}
            />
            <div className="tags-container">
              {filteredTags.length === 0 ? (
                <small style={{ color: 'var(--text-muted)', padding: '10px 0', display: 'block' }}>
                  {tagSearch ? 'Ничего не найдено' : 'Загрузка тегов...'}
                </small>
              ) : (
                filteredTags.map(tag => (
                  <button
                    key={tag.pk_tag}
                    type="button"
                    className={`tag-chip ${selectedTags.includes(tag.pk_tag) ? 'selected' : ''}`}
                    onClick={() => toggleTag(tag.pk_tag)}
                  >
                    {tag.title}
                  </button>
                ))
              )}
            </div>
            {selectedTags.length > 0 && (
              <small className="selected-tags-info">Выбрано: {selectedTags.length}</small>
            )}
          </div>

          {/* Сообщения */}
          {error && <div className="form-message error">{error}</div>}
          {successMsg && <div className="form-message success">{successMsg}</div>}

          {/* Кнопка */}
          <button
            className="form-btn"
            type="submit"
            disabled={loading || photos.length === 0 || !formData.fk_type}
            style={{ marginTop: '20px' }}
          >
            {loading ? 'Загрузка...' : 'Опубликовать'}
          </button>
        </form>
      </div>
    </div>
  );
};